import React, { useEffect, useState, useRef, useCallback } from 'react';
import { supabase, callAdminFunction } from '../supabaseClient';
import Navbar from './Navbar';
import './Empleados.css';
import EditEmployeeModal from './EditEmployeeModal';
import { FaSearch } from 'react-icons/fa';
import { FaEdit } from "react-icons/fa";
import { TiDelete } from "react-icons/ti";
import EmployeeDetailsModal from './EmployeeDetailsModal';
import { checkAndUpdateEmployeeData } from './employeeUpdates';
import { getCurrentDate, USE_TEST_DATE } from './employeeUpdates';
import { BiMailSend } from "react-icons/bi";
import { sendVacationInfoEmail } from './emailService';
import CreateUserModal from './CreateUserModal';
import { calculateDaysTaken } from '../utils/daysTakenCalculation';



function Empleados() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [currentPage, setCurrentPage] = useState(0); // Track current page
  const [hasMore, setHasMore] = useState(true); // Track if there are more rows
  const ROWS_PER_PAGE = 10; // Number of rows per page
  const [searchTerm, setSearchTerm] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(false); //Track if the page is loading
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentDate, setCurrentDate] = useState('');
  const [userRole, setUserRole] = useState('');
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newEmployeeEmail, setNewEmployeeEmail] = useState('');

  const isLoadingRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  // Function to get the user's role (similar to Navbar.js)
  const getUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('wsp_email', user.email)
          .single();

        if (error) throw error;
        setUserRole(data.role);
      }
    } catch (error) {
      console.error('Error fetching user role:', error.message);
    }
  };

  // Add useEffect to get the role when component mounts
  useEffect(() => {
    getUserRole();
  }, []);

  // Modified handleDeactivate function
  const handleDeactivate = async (employee) => {
    try {
      // First delete related vacation days records
      const { error: vacationDaysError } = await supabase
        .from('vacation_days')
        .delete()
        .eq('employee_id', employee.id);

      if (vacationDaysError) throw vacationDaysError;

      // Delete all vacation requests associated with this employee
      const { error: vacationRequestsError } = await supabase
        .from('vacation_requests')
        .delete()
        .eq('employee_id', employee.id);

      if (vacationRequestsError) throw vacationRequestsError;

      // Next deactivate the employee in the employees table
      const { error: deactivateError } = await supabase
        .from('employees')
        .update({ active: false })
        .eq('wsp_email', employee.wsp_email);

      if (deactivateError) throw deactivateError;

      // Now delete the user's credentials
      // 1. Get the auth user info
      const { data: authUsers } = await callAdminFunction('listUsers', {});

      // 2. Find the matching user by email
      const authUser = authUsers.users.find(user => user.email === employee.wsp_email);

      if (authUser) {
        // 3. Delete from Supabase auth if found
        await callAdminFunction('deleteUser', { userId: authUser.id });
      }

      // 4. Delete from users table
      const { error: dbError } = await supabase
        .from('users')
        .delete()
        .eq('wsp_email', employee.wsp_email);

      if (dbError && dbError.code !== 'PGRST116') {
        // Skip error if user not found in users table
        console.warn("User not found in users table or already deleted");
      }

      // 5. Refresh employee list
      fetchEmployees(currentPage, searchTerm);
    } catch (error) {
      console.error("Error in handleDeactivate:", error);
      setError('Error deactivating employee: ' + error.message);
    }
  };

  // function to handle the update of rows with the id of the employee
  const handleUpdate = async (updatedData, originalId) => {
    try {
      const { error } = await supabase
        .from('employees')
        .update(updatedData)
        .eq('id', originalId);

      if (error) throw error;
      // Pass the current page and search term when refreshing the data
      fetchEmployees(currentPage, searchTerm);
      setEditingEmployee(null);
    } catch (error) {
      setError('Error updating employee: ' + error.message);
    }
  };

  // function to handle creating new employee
  const handleCreate = async (newEmployeeData, createUserAccount = false) => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .insert([newEmployeeData])
        .select();

      if (error) throw error;

      // For new entries, you might want to go to the first page
      setCurrentPage(0);
      fetchEmployees(0, searchTerm);
      setIsCreating(false);
      
      // If user checked the create user account toggle, show the create user modal
      if (createUserAccount) {
        setNewEmployeeEmail(newEmployeeData.wsp_email);
        setShowCreateUserModal(true);
      }
    } catch (error) {
      setError('Error creating employee: ' + error.message);
    }
  };

  // Add a new function to handle user creation completion
  const handleUserCreated = async (userData) => {
    setShowCreateUserModal(false);
    // You could show a success message here if desired
    return true;
  };

  // Fetch employees from the database using pagination
  const fetchEmployees = useCallback(async (page = 0, search = '') => {
    // Prevent duplicate fetches
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;

    setIsPageLoading(true);
    setLoading(true);

    try {
      const from = page * ROWS_PER_PAGE;
      const to = from + ROWS_PER_PAGE - 1;

      // Apply role-based filtering
      let query = supabase
        .from('employees')
        .select('*', { count: 'exact' })
        .eq('active', true);

      // Filter based on user role
      if (userRole === 'admin' || userRole === 'RH') {
        // No additional filter - can see all employees
      } else if (userRole === 'CEO') {
        query = query.in('area', ['Administrativo / Dirección', 'Comercial', 'Ventas']);
      } else if (['Fábrica ABAP', 'Staffing', 'PMO-IT', 'Finanzas'].includes(userRole)) {
        // For these roles, they can only see employees in their own area
        query = query.eq('area', userRole);
      }

      // Apply search filter if any
      if (search) {
        query = query.ilike('wsp_email', `%${search}%`);
      }

      // First, get the total count with the same filters
      const { data, error: countError, count } = await query
        .range(0, 0) // Just get the first record for counting
        .limit(1);   // Limit to 1 record for efficiency

      if (countError) throw countError;
      setTotalCount(count || 0);

      if (count === 0 || from >= count) {
        setEmployees([]);
        setIsNextDisabled(true);
        setIsPrevDisabled(page === 0);
        setHasMore(false);
        setLoading(false);
        setIsPageLoading(false);
        isLoadingRef.current = false;
        return;
      }

      // Then fetch the employees with pagination
      const { data: fetchedEmployees, error } = await query
        .range(from, to)
        .order('full_name', { ascending: true });

      if (error) throw error;

      // Process all employees in a single batch
      const updateResult = await checkAndUpdateEmployeeData(fetchedEmployees);

      if (!updateResult.success) {
        console.error('Updates failed:', updateResult.errors);
        setError('Error al actualizar algunos empleados');
      } else {
        // Calculate days taken for each employee
        const employeesWithCalculatedDays = await Promise.all(
          updateResult.updatedEmployees.map(async (employee) => {
            const calculatedDaysTaken = await calculateDaysTaken(
              employee.renovation_day,
              employee.id
            );
            return {
              ...employee,
              calculated_days_taken: calculatedDaysTaken
            };
          })
        );

        // Update all states in a single batch
        setEmployees(employeesWithCalculatedDays);
        setIsNextDisabled(from + employeesWithCalculatedDays.length >= count);
        setIsPrevDisabled(page === 0);
        setHasMore(from + employeesWithCalculatedDays.length < count);
        setError(null);
      }

    } catch (error) {
      console.error('Error:', error);
      setError('Error fetching employees: ' + error.message);
    } finally {
      setLoading(false);
      setIsPageLoading(false);
      isLoadingRef.current = false;
    }
  }, [userRole]); // Add userRole as a dependency


  // Modify the useEffect that fetches employees on initial load
  useEffect(() => {
    // Only fetch employees when userRole is available
    if (userRole) {
      // Clear any existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for search
      searchTimeoutRef.current = setTimeout(() => {
        fetchEmployees(0, searchTerm);
      }, 500); // 500ms delay
    }

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, fetchEmployees, userRole]); // Add userRole as a dependency

  // New useEffect for date formatting
  useEffect(() => {
    const date = new Date(getCurrentDate());
    date.setDate(date.getDate() + 1);
    const options = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    const formattedDate = date.toLocaleDateString('es-MX', options);
    const capitalizedDate = formattedDate.replace(/^\w/, (c) => c.toUpperCase());
    setCurrentDate(capitalizedDate);
  }, []);


  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Add one day to compensate for timezone
    date.setDate(date.getDate() + 1);
    return date.toLocaleDateString('es-MX');
  };

  // Pagination handlers
  const handlePreviousPage = useCallback(() => {
    if (currentPage > 0 && !isPrevDisabled && !isPageLoading && !isLoadingRef.current) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      fetchEmployees(newPage, searchTerm);
    }
  }, [currentPage, isPrevDisabled, isPageLoading, searchTerm, fetchEmployees]);

  const handleNextPage = useCallback(() => {
    if (hasMore && !isNextDisabled && !isPageLoading && !isLoadingRef.current) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      fetchEmployees(newPage, searchTerm);
    }
  }, [currentPage, hasMore, isNextDisabled, isPageLoading, searchTerm, fetchEmployees]);

  // Search handler
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  }, []);



  // Render the component
  return (
    <div className="empleados">
      <Navbar />
      <div className="empleados-content">
        <div className="empleados-header">
          <div className="search-container">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="search"
                placeholder="Buscar empleado por correo WSP"
                className="search-input"
                value={searchTerm}
                onChange={handleSearchChange}
                disabled={loading || isPageLoading}
              />
            </div>
          </div>

          {/* Add the date display */}
          <div className="current-date">
            {USE_TEST_DATE ? '(TEST) ' : ''}Hoy es {currentDate}
          </div>

          {(userRole === 'admin' || userRole === 'RH') && (
            <button
              className={`new-employee-button ${(loading || isPageLoading) ? 'disabled' : ''}`}
              onClick={() => setIsCreating(true)}
              disabled={loading || isPageLoading}
            >
              Nuevo Empleado +
            </button>
          )}
        </div>

        <div className="table-container">
          {error && <div className="error-message">{error}</div>}
          {loading ? (
            <div className="loading-message">Cargando empleados...</div>
          ) : (
            <table className="empleados-table">
              <thead>
                <tr>
                  <th>Nombre Completo</th>
                  <th>Correo WSP</th>
                  <th>Días ya tomados<br />(Periodo Actual)</th>
                  <th>Días Disponibles<br />(+ prórroga)</th>
                  <th>Renovación<br />(dd/mm/yyyy)</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <tr key={employee.wsp_email}>
                    <td data-label="Nombre Completo">
                      <button
                        className="name-button"
                        onClick={() => setViewingEmployee(employee)}
                      >
                        {employee.full_name}
                      </button>
                    </td>
                    <td data-label="Correo WSP">{employee.wsp_email}</td>
                    <td data-label="Días ya tomados">{employee.calculated_days_taken} Días</td>
                    <td data-label="Días Disponibles">{employee.remaining_days} Días</td>
                    <td data-label="Renovación">{formatDate(employee.renovation_day)}</td>
                    <td data-label="Acciones">
                      <div className="action-buttons">
                        <button
                          className="email-button"
                          title="Enviar información de vacaciones"
                          onClick={() => {
                            try {
                              sendVacationInfoEmail(employee);
                            } catch (error) {
                              console.error('Error sending email:', error);
                            }
                          }}
                        >
                          <BiMailSend className="email-icon" />
                        </button>

                        {(userRole === 'admin' || userRole === 'RH') && (
                          <button
                            className="edit-button"
                            title="Editar"
                            onClick={() => setEditingEmployee(employee)}
                          >
                            <FaEdit className="edit-icon" />
                          </button>
                        )}

                        {(userRole === 'admin' || userRole === 'RH') && (
                          <button
                            className="delete-button"
                            title="Desactivar"
                            onClick={() => {
                              if (window.confirm(
                                '¿Está seguro que desea desactivar este empleado? \n\n'
                                + employee.full_name + ' - ' + employee.wsp_email 
                                + '\n\nEsta acción también eliminará las credenciales de acceso de este empleado.'
                                + '\n\nTambién se eliminarán todos los registros históricos de días de vacaciones.'
                                + '\n\n(El empleado se puede volver a activar desde el apartado de "Reactivación", '
                                + 'pero tendrá que crear nuevas credenciales si se requiere acceso)')
                              ) {
                                handleDeactivate(employee);
                              }
                            }}
                          >
                            <TiDelete className="delete-icon" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="pagination">
          <button
            className={`pagination-button ${isPrevDisabled ? 'disabled' : ''}`}
            onClick={handlePreviousPage}
            disabled={isPrevDisabled || isPageLoading}
          >
            Atrás
          </button>
          <span className="page-info">
            Página {currentPage + 1} de {Math.ceil(totalCount / ROWS_PER_PAGE)}
          </span>
          <button
            className={`pagination-button ${isNextDisabled ? 'disabled' : ''}`}
            onClick={handleNextPage}
            disabled={isNextDisabled || isPageLoading}
          >
            Siguiente
          </button>
        </div>

        {/* Create Employee Modal */}
        {isCreating && (
          <EditEmployeeModal
            employee={null}
            onClose={() => setIsCreating(false)}
            onSave={handleCreate}
            isNew={true}
          />
        )}

        {/* Edit Employee Modal */}
        {editingEmployee && (
          <EditEmployeeModal
            employee={editingEmployee}
            onClose={() => setEditingEmployee(null)}
            onSave={(updatedData) => handleUpdate(updatedData, editingEmployee.id)}
          />
        )}

        {/* Employee Details Modal */}
        {viewingEmployee && (
          <EmployeeDetailsModal
            employee={viewingEmployee}
            onClose={() => setViewingEmployee(null)}
          />
        )}
        
        {/* New Create User Modal that appears after employee creation */}
        {showCreateUserModal && (
          <CreateUserModal
            onClose={() => setShowCreateUserModal(false)}
            onSave={handleUserCreated}
            userType="Empleado"
            prefilledEmail={newEmployeeEmail}
          />
        )}
      </div>
    </div>
  );
}

export default Empleados;